<template>
  <div
    data-track-as="share"
    :class="[
      'sanitas-magazine-social',
      {
        overlay: socialMagazineProps.visibility,
      },
    ]"
  >
    <div v-if="socialMagazineProps.visibility" class="social-close">
      <a href data-track-skip @click.prevent="hideSocial">
        <dotcom-icon name="xmark" art="light" color="--c-secondary-neutral-2" />
      </a>
    </div>
    <div class="sanitas-magazine-social-divider">
      <slot name="title"></slot>
    </div>
    <div class="sanitas-magazine-social-container">
      <template v-for="(social, i) in socialLinks">
        <a
          :key="i"
          :data-track-text="social.title.toLowerCase()"
          class="sanitas-magazine-social-container__link"
          target="_blank"
          :href="
            social.title !== 'Email'
              ? share(social.title, social.link)
              : emailCurrentPage(social.link)
          "
        >
          <span class="sanitas-magazine-social-container__icon-wrapper">
            <dotcom-icon
              class="sanitas-magazine-social-container__icon"
              :name="social.icon"
              transform="shrink-6"
            />
          </span>
        </a>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SanitasMagazineSocialSharing',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },

  props: {
    links: {
      type: String,
      default: '',
    },
    socialMagazineProps: {
      type: Object,
      default: () => {
        return {
          visibility: false,
        }
      },
    },
  },

  data() {
    return {
      socialLinks: [],
      shareLinks: {
        whatsapp: 'https://api.whatsapp.com/send?text=',
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=',
        twitter: 'https://twitter.com/share?text=',
        xing: 'https://www.xing.com/spi/shares/new?url=',
        linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=',
      },
    }
  },

  mounted() {
    if (this.links) {
      this.socialLinks = JSON.parse(this.links)
    } else {
      this.socialLinks = [
        {
          title: 'Whatsapp',
          icon: 'fab-whatsapp',
          link: '#',
        },
        {
          title: 'Facebook',
          icon: 'fab-facebook-f',
          link: '#',
        },
        {
          title: 'Twitter',
          icon: 'fab-twitter',
          link: '#',
        },
        {
          title: 'Xing',
          icon: 'fab-xing',
          link: '#',
        },
        {
          title: 'Linkedin',
          icon: 'fab-linkedin-in',
          link: '#',
        },
        {
          title: 'Email',
          icon: 'fal-envelope',
          link: '#',
        },
      ]
    }
  },

  methods: {
    emailCurrentPage(link) {
      return link === '#'
        ? link
        : `mailto:?subject=${document.title}&body=${this.escapeHtml(
            window.location.href,
          )}`
    },

    hideSocial() {
      this.$emit('hideSocial')
    },

    share(type, link) {
      if (link === '#') {
        return link
      }
      return (
        `${this.shareLinks[type.toLowerCase()]}${this.escapeHtml(
          window.location.href,
        )}` || ''
      )
    },

    escapeHtml(unsafe) {
      return unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
  },
}
</script>

<style lang="postcss">
.sanitas-magazine-social-divider {
  display: flex;
  font-size: 20px;
  font-weight: bold;
  color: var(--c-primary-neutral-1);
  justify-content: center;
  padding-bottom: var(--spacing-4);
}

.overlay .sanitas-magazine-social-divider {
  padding-bottom: var(--spacing-8);
}

.sanitas-magazine-social {
  padding: var(--spacing-6) 0 var(--spacing-8);

  &.overlay {
    height: 220px;
    background-color: white;
    border-bottom: 1px solid var(--c-primary-neutral-1);
    position: relative;
    z-index: 10;
    padding: 30px 30px 75px;

    .social-close {
      position: absolute;
      top: 15px;
      right: 15px;

      a:hover {
        text-decoration: none;
        border-bottom: none;
      }
    }

    .sanitas-magazine-social-divider,
    .sanitas-magazine-social-container {
      max-width: 800px;
      margin-left: auto;
      margin-right: auto;
      justify-content: left;
    }
  }

  &-container {
    display: flex;
    justify-content: center;
    gap: var(--spacing-2);

    a {
      position: relative;
      overflow: visible;

      &:hover {
        border-bottom: none;
      }
    }
  }
}
</style>

<style scoped>
.sanitas-magazine-social-container__icon-wrapper {
  --magazine-social-sharing-icon-size: 36px;
  display: inline-flex;
  justify-content: center;
  width: var(--magazine-social-sharing-icon-size);
  color: var(--c-primary-neutral-1);
  border-radius: 50%;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: var(--magazine-social-sharing-icon-size);
    height: var(--magazine-social-sharing-icon-size);
    border-radius: 50%;
    border: 1px solid var(--c-primary-neutral-1);
  }
}

.sanitas-magazine-social-container__link:hover {
  .sanitas-magazine-social-container__icon-wrapper {
    color: var(--c-primary-neutral-3);
    background-color: var(--c-primary-color-3);

    &::after {
      border-color: var(--c-primary-color-3);
    }
  }
}

.sanitas-magazine-social-container__icon {
  flex-shrink: 0;
  font-size: var(--magazine-social-sharing-icon-size);
}

@media (--sm) {
  .sanitas-magazine-social-container__icon-wrapper {
    --magazine-social-sharing-icon-size: 45px;
  }
}
</style>
